/**
 * The state and keyboard of a checklist: a short list of options opened from
 * a trigger, ticked from the keyboard or the pointer. Stays open while picking
 * several; picking the one value of a single-choice list closes it.
 *
 * Focus stays on the list once open. The arrow keys, Home and End move a
 * highlight, which the list names to assistive tech through
 * aria-activedescendant; Space or Enter ticks it; a letter jumps to the next
 * option it starts; Escape (the popover's own) or Tab closes.
 */

import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { dispatchCellMove, findGridRoot, focusGrid } from '../dom';
import type {
  ChecklistClearAll,
  ChecklistFooter,
  ChecklistOption,
} from './types';

type ChecklistOptions = {
  options: readonly ChecklistOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** One value at a time; picking closes the list. */
  single?: boolean;
  /** In a grid cell, Tab closes the list and moves the grid's cursor on. */
  gridCell?: boolean;
  /** Takes no picks; a list open when this turns on closes. */
  disabled?: boolean;
  /** A last row that closes the list and runs an action. */
  footer?: ChecklistFooter;
  /** "Clear all" with its own action, instead of unticking what is shown. */
  clearAll?: ChecklistClearAll;
};

export function useChecklist({
  options,
  value,
  onChange,
  single = false,
  gridCell = false,
  disabled = false,
  footer,
  clearAll: customClear,
}: ChecklistOptions) {
  const [open, setOpen] = useState(false);
  // Disabled while open (the row was locked by a save elsewhere, say): the
  // list closes. Adjusted while rendering, so no frame shows it open; the
  // popover's close then hands the keys back (see ChecklistPopover).
  if (disabled && open) setOpen(false);
  // The highlighted row: an option, or `clearIndex` for "Clear all".
  const [active, setActive] = useState(0);
  const idPrefix = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // A multiple-choice list ends with "Clear all": always there, greyed when
  // nothing is picked, since a row that came and went with the first tick
  // would shift the list under the pointer.
  const clearIndex = single ? null : options.length;
  const footerIndex = footer ? (clearIndex ?? options.length - 1) + 1 : null;
  const lastIndex = Math.max(
    0,
    footerIndex ?? clearIndex ?? options.length - 1
  );
  // Values with no option (an archived or filtered-out entry) still count as
  // picked: they are kept by every tick and removed by "Clear all".
  const nothingPicked = customClear ? customClear.disabled : value.length === 0;

  /**
   * Whether the keys are still this checklist's to hand back: in its list, on
   * its own trigger (a header button the grid's keys ignore), or nowhere. Not
   * when they have gone to another field, or wherever a Tab sent them.
   */
  const keysAreHere = () => {
    const focused = document.activeElement;
    return (
      focused === document.body ||
      focused === triggerRef.current ||
      !!listRef.current?.contains(focused)
    );
  };

  // Hand the keys back to the grid the moment the list closes, not when the
  // popover has finished animating out: until then it keeps focus, and a fast
  // next key would land in the old list.
  const close = () => {
    if (keysAreHere()) focusGrid(findGridRoot(triggerRef.current));
    setOpen(false);
  };

  // Opens on the first picked option, or the top of the list. Ticking later
  // must not move the highlight, so this happens only on opening.
  const onOpenChange = (next: boolean) => {
    if (!next) {
      close();
      return;
    }
    if (disabled) return;
    const first = options.findIndex(option => value.includes(option.value));
    setActive(first >= 0 ? first : 0);
    setOpen(true);
  };

  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // Every write is checked against `disabled`, not only the opening: a click
  // or key can still reach a list that is closing.
  const toggle = (option: ChecklistOption) => {
    if (disabled) return;
    if (single) {
      onChange([option.value]);
      close();
      return;
    }
    if (value.includes(option.value)) {
      onChange(value.filter(v => v !== option.value));
      return;
    }
    // Kept in the options' order, whatever order they were ticked in, with
    // any values that have no option left after them rather than dropped.
    const known = new Set(options.map(o => o.value));
    onChange([
      ...options
        .map(o => o.value)
        .filter(v => v === option.value || value.includes(v)),
      ...value.filter(v => !known.has(v)),
    ]);
  };
  const clearAll = () => {
    if (disabled || nothingPicked) return;
    if (customClear) customClear.run();
    else onChange([]);
  };

  const runFooter = () => {
    if (disabled || !footer) return;
    close();
    footer.onSelect();
  };

  const activate = (index: number) => {
    if (index < options.length) toggle(options[index]);
    else if (index === clearIndex && !nothingPicked) clearAll();
    else if (index === footerIndex) runFooter();
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    // The list stays on screen while it animates out; it takes no keys then.
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive(i => Math.min(lastIndex, i + 1));
        return;
      case 'ArrowUp':
        e.preventDefault();
        setActive(i => Math.max(0, i - 1));
        return;
      case 'Home':
        e.preventDefault();
        setActive(0);
        return;
      case 'End':
        e.preventDefault();
        setActive(lastIndex);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        activate(active);
        return;
      case 'Tab':
        e.preventDefault();
        close();
        // In a grid, Tab carries on to the next cell as it does elsewhere.
        // The grid has the keys back by now, so the move can take them on
        // past its edge.
        if (gridCell) {
          dispatchCellMove(triggerRef.current, {
            rows: 0,
            cols: e.shiftKey ? -1 : 1,
          });
        }
        return;
    }
    // A letter jumps to the next option starting with it.
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const letter = e.key.toLowerCase();
      const count = options.length;
      for (let step = 1; step <= count; step++) {
        const index = (active + step) % count;
        if (options[index].label.toLowerCase().startsWith(letter)) {
          setActive(index);
          return;
        }
      }
    }
  };

  // Only a row that is drawn can be named as the highlighted one.
  const activeIsDrawn =
    active < options.length || active === clearIndex || active === footerIndex;

  return {
    options,
    value,
    open,
    keysAreHere,
    onOpenChange,
    /** Opens the list, as a double click on a grid cell does. */
    openList: () => onOpenChange(true),
    active,
    setActive,
    /** A row's stable id, for aria-activedescendant. */
    itemId: (index: number) => `${idPrefix}-item-${index}`,
    /** The highlighted row's id, or undefined when no row is drawn there. */
    activeId: activeIsDrawn ? `${idPrefix}-item-${active}` : undefined,
    triggerRef,
    listRef,
    onListKeyDown,
    toggle,
    /** The "Clear all" row, or null for a single-choice list. */
    clear:
      clearIndex === null
        ? null
        : { index: clearIndex, disabled: nothingPicked, run: clearAll },
    /** The action row after the options, or null when there is none. */
    footer:
      footer && footerIndex !== null
        ? { index: footerIndex, label: footer.label, run: runFooter }
        : null,
    single,
  };
}

export type Checklist = ReturnType<typeof useChecklist>;
